import { Location } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav/sidenav';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { SimpleUserDto } from './../../../data/models/dto/simple-user.dto';
import { ChatService } from './../../../data/services/chat.service';
import { ContractedFeatureUtil } from './../../../data/utils/contracted-feature.utils';
import {
  ALL_PROFILES,
  BUSINESS_PROFILES,
  EMPLOYEES_PROFILES,
  MANAGEMENT_ADVISOR_PROFILES,
  MANAGEMENT_PROFILES,
  RESTRICTED_ROUTE_PROFILES,
} from './../../../data/utils/profile.utils';
import { UserUtil } from './../../../data/utils/user.utils';
import { UserHeaderDto } from '../../../data/models/dto/user-header.dto';
import { AuthService } from '../../../data/services/auth.service';
import { ChatsMessagesService } from './../../../data/services/chats-messages.service';
import { AuthUtil } from '../../../data/utils/auth.utils';
import { MatDialog } from '@angular/material/dialog';
import { SelectTenantDialogComponent } from '../../pages/auth/select-tenant-dialog/select-tenant-dialog.component';
import { filter } from 'rxjs/operators';
import {CalendarIntegrationUtil} from "../../../data/utils/calendar-integration-util";

@Component({
  selector: 'app-nav-sidebar',
  templateUrl: './nav-sidebar.component.html',
  styleUrls: ['./nav-sidebar.component.scss'],
})
export class NavSidebarComponent implements OnInit {
  features: {
    title: string;
    icon: string;
    route: string;
    roles?: string[];
    unread$?: Observable<string>;
  }[] = [
    {
      title: 'Notícias',
      icon: 'home',
      route: 'news-list',
      roles: RESTRICTED_ROUTE_PROFILES(MANAGEMENT_ADVISOR_PROFILES),
    },
    {
      title: 'Calendário',
      icon: 'event',
      route: 'events-list',
      roles: RESTRICTED_ROUTE_PROFILES(MANAGEMENT_ADVISOR_PROFILES),
    },
    {
      title: 'Atendimento',
      icon: 'chat',
      route: 'chat',
      roles: ALL_PROFILES,
      unread$: this.chatService.getNumMessagesUnread(),
    },
    {
      title: 'Usuários do sistema',
      icon: 'person',
      route: 'users',
      roles: RESTRICTED_ROUTE_PROFILES(EMPLOYEES_PROFILES),
    },

    {
      title: 'Aplicativo',
      icon: 'phone_android',
      route: 'links',
      roles: RESTRICTED_ROUTE_PROFILES(MANAGEMENT_PROFILES),
    },

    {
      title: 'Sair',
      icon: 'logout',
      route: '',
      roles: ALL_PROFILES,
    },
  ];

  featuresFilter: Set<any> = new Set();

  title = 'Agenda';
  disableDrawer = false;
  close = false;

  // @ViewChild('o')
  // outlet!: RouterOutlet;

  @ViewChild('drawer')
  drawer!: MatSidenav;

  user: SimpleUserDto | undefined;

  userHeaderDto: UserHeaderDto | undefined;

  currentTenant: string | undefined;
  hasMultipleTenants: boolean = false;
  panelOpenState = false;

  constructor(
    private readonly router: Router,
    private readonly location: Location,
    private readonly authService: AuthService,
    private readonly contractedFeatureUtil: ContractedFeatureUtil,
    private readonly userUtil: UserUtil,
    private readonly authUtil: AuthUtil,
    private readonly chatService: ChatService,
    private readonly chatmessageservice: ChatsMessagesService,
    private readonly dialog: MatDialog,
    private readonly activatedRoute: ActivatedRoute,
    private readonly calendarIntegrationUtil: CalendarIntegrationUtil,
  ) {}

  ngOnInit(): void {
    this.user = this.userUtil.getUser();
    this.currentTenant = this.authUtil.getTenant();
    this.hasMultipleTenants = this.authUtil.getIdentities().length > 1;
    this.contractedFeaturesMenu();
    this.filterRouter();
    this.getTotalUnreadMessages();
    this.listenRouteChanges();
  }

  contractedFeaturesMenu(): void {
    if (this.contractedFeatureUtil.hasCheguei()) {
      this.features.splice(3, 0, {
        title: 'Cheguei',
        icon: 'pin_drop',
        route: 'gate-list',
        roles: RESTRICTED_ROUTE_PROFILES(BUSINESS_PROFILES),
      });
    }

    if (this.contractedFeatureUtil.hasSeatBookings()) {
      this.features.splice(3, 0, {
        title: 'Eventos',
        icon: 'theaters',
        route: 'shows',
        roles: RESTRICTED_ROUTE_PROFILES(EMPLOYEES_PROFILES),
      });
    }
  }

  onFeatureClick(_: MouseEvent, feature: any): void {
    if (feature.title === 'Sair') {
      this.logout();
      return;
    }
    this.drawer.toggle();
  }

  clickMenu(): void {
    let route = this.activatedRoute;

    while (route.firstChild) {
      route = route.firstChild;
    }

    const data = route.snapshot.data;

    if (data?.backPath) {
      this.router.navigate([data.backPath]);
      return;
    }

    data?.disableDrawer || data?.close
      ? this.location.back()
      : this.drawer.toggle();
  }

  filterRouter() {
    const hasCalendarIntegration = this.calendarIntegrationUtil.getCalendar();

    this.features.filter((data) => {
      this.user?.profiles.forEach((profile: string) => {
        if (data.roles?.includes(profile)) {
          if (!hasCalendarIntegration) {
            this.featuresFilter.add(data);
          } else if (hasCalendarIntegration && data.route !== 'events-list') {
            this.featuresFilter.add(data);
          }
        }
      });
    });
  }

  setFormartedNames(profiles: any): string {
    return profiles.join().replace(',', '\n');
  }

  onChangeTenant(): void {
    this.dialog.open(SelectTenantDialogComponent, {
      height: '450px',
      panelClass: 'select-tenant-dialog',
    });
  }

  private listenRouteChanges(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.resolveRouteData();
      });

    // garante no primeiro load
    this.resolveRouteData();
  }

  private async logout(): Promise<void> {
    await this.authService.logout();
    await this.router.navigate(['/login']);
  }

  private async getTotalUnreadMessages() {
    const totalUnread = await this.chatmessageservice
      .getTotalUnreadMessages()
      .toPromise();
    this.chatService.setNumMessagesUnread(totalUnread);
  }

  private resolveRouteData(): void {
    let route = this.activatedRoute;

    while (route.firstChild) {
      route = route.firstChild;
    }

    const data = route.snapshot.data;

    this.title = data['title'] ?? 'Agenda';
    this.userHeaderDto = data['userHeaderDto'] ?? null;
    this.disableDrawer = !!data['disableDrawer'];
    this.close = !!data['close'];
  }
}
